import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../utils/api';
import SearchableSelect from './SearchableSelect';
import './PromptSelectModal.css';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

/**
 * プロンプト選択モーダル
 * 
 * AI処理実行前にプロンプトを選択するための汎用コンポーネント
 * 
 * @example
 * <PromptSelectModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   onConfirm={(promptId) => handleExecute(promptId)}
 *   promptType="summary"
 *   title="AI要約を実行"
 *   message="使用するプロンプトを選択してください"
 * />
 */
function PromptSelectModal({
    isOpen,
    onClose,
    onConfirm,
    promptType,
    title = 'AI処理を実行',
    message = '使用するプロンプトを選択してください',
    confirmButtonText = '実行',
    cancelButtonText = 'キャンセル',
    secondaryOptions,
    secondaryLabel = '追加選択',
    secondaryPlaceholder = '選択してください...',
    secondaryDefaultLabel = '指定なし',
}) {
    const [prompts, setPrompts] = useState([]);
    const [selectedPromptId, setSelectedPromptId] = useState('');
    const [selectedSecondaryValue, setSelectedSecondaryValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);

    // onConfirmの安定参照（呼び出し元がuseCallbackでラップしていなくても安全）
    const onConfirmRef = useRef(onConfirm);
    onConfirmRef.current = onConfirm;

    // プロンプト一覧を取得
    // request.cancelled は、この取得を始めた effect が片付けられたことを示す。古い取得の結果は捨てる。
    // 下の自動確定は AI 処理を始めるため、古い取得から走らせると同じ処理が 2 回始まる
    // （React の StrictMode は開発時に effect を 2 回走らせる。閉じたり種類が変わったりした後に応答が届くこともある）
    const fetchPrompts = useCallback(async (request) => {
        if (!promptType) return;

        setLoading(true);
        setError(null);

        try {
            const response = await api.fetch(`/prompts/selection?prompt_type=${promptType}`);
            if (request.cancelled) return;
            if (response && response.ok) {
                const data = await response.json();
                if (request.cancelled) return;
                const fetchedPrompts = data.prompts || [];
                setPrompts(fetchedPrompts);

                // プロンプトが0件、または（1件かつ追加選択対象がない場合） → モーダルをスキップ
                // ※ 求人選択(secondaryOptions)がある場合は、プロンプトが1件でもスキップせずに表示する
                const hasSecondary = secondaryOptions && secondaryOptions.length > 0;
                if (fetchedPrompts.length === 0 || (fetchedPrompts.length === 1 && !hasSecondary)) {
                    const passPromptId = fetchedPrompts.length === 1 ? fetchedPrompts[0].id : '';
                    onConfirmRef.current(passPromptId, '');
                    return;
                }

                // 先頭のプロンプトを自動選択
                setSelectedPromptId(fetchedPrompts[0].id);
            } else {
                setError('プロンプト一覧の取得に失敗しました');
            }
        } catch (err) {
            if (request.cancelled) return;
            console.error('Failed to fetch prompts:', err);
            setError('プロンプト一覧の取得に失敗しました');
        } finally {
            if (!request.cancelled) {
                setLoading(false);
                setIsInitialFetchDone(true);
            }
        }
    }, [promptType, secondaryOptions]);

    // モーダルが開かれたときにプロンプトを取得し、閉じたときはフラグをリセット
    useEffect(() => {
        if (!isOpen) {
            setIsInitialFetchDone(false);
            return undefined;
        }
        const request = { cancelled: false };
        fetchPrompts(request);
        return () => {
            request.cancelled = true;
        };
    }, [isOpen, fetchPrompts]);

    // 実行ボタンクリック
    const handleConfirm = () => {
        onConfirm(selectedPromptId, selectedSecondaryValue);
    };

    // モーダル表示中は背景スクロールを禁止
    useBodyScrollLock(isOpen);

    if (!isOpen) return null;

    if (loading || !isInitialFetchDone) {
        return (
            <div className="modal-overlay" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <span className="spinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }}></span>
            </div>
        );
    }


    return (
        <div className="modal-overlay">
            <div className="modal-content prompt-select-modal">
                <button
                    type="button"
                    className="modal-close-x"
                    onClick={onClose}
                    aria-label="閉じる"
                >
                    ✕
                </button>
                <h2 className="modal-title">{title}</h2>

                <p className="prompt-select-message">{message}</p>

                {error && (
                    <div className="modal-error-message">{error}</div>
                )}

                {/* 追加選択肢（secondaryOptions が渡された場合のみ表示） */}
                {secondaryOptions && (
                    <div className="form-group">
                        <label htmlFor="secondary-select">{secondaryLabel}</label>
                        <SearchableSelect
                            id="secondary-select"
                            options={[
                                { value: '', label: secondaryDefaultLabel },
                                ...secondaryOptions,
                            ]}
                            value={selectedSecondaryValue}
                            onChange={setSelectedSecondaryValue}
                            placeholder={secondaryPlaceholder}
                        />
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="prompt-select">プロンプト</label>
                    <SearchableSelect
                        id="prompt-select"
                        options={prompts.map((p) => ({
                            value: p.id,
                            label: `${p.name}${p.is_system ? '（システム）' : ''}`
                        }))}
                        value={selectedPromptId}
                        onChange={setSelectedPromptId}
                        disabled={loading}
                        placeholder="使用するプロンプトを選択..."
                        searchPlaceholder="プロンプト名で検索..."
                        noResultsText="該当するプロンプトがありません"
                    />
                </div>

                <div className="modal-actions">
                    <button
                        type="button"
                        className="modal-button-cancel"
                        onClick={onClose}
                    >
                        {cancelButtonText}
                    </button>
                    <button
                        type="button"
                        className="modal-button-confirm"
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading ? '読み込み中...' : confirmButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
}

PromptSelectModal.propTypes = {
    /** モーダルの表示状態 */
    isOpen: PropTypes.bool.isRequired,
    /** モーダルを閉じるコールバック */
    onClose: PropTypes.func.isRequired,
    /** 実行ボタン押下時のコールバック（promptId, jobId を引数で渡す） */
    onConfirm: PropTypes.func.isRequired,
    /** プロンプト種別（summary, feedback等） */
    promptType: PropTypes.string.isRequired,
    /** モーダルタイトル */
    title: PropTypes.string,
    /** 説明メッセージ */
    message: PropTypes.string,
    /** 実行ボタンのテキスト */
    confirmButtonText: PropTypes.string,
    /** キャンセルボタンのテキスト */
    cancelButtonText: PropTypes.string,
    /** 追加選択肢 [{value, label}]。渡された場合のみ追加選択UIを表示 */
    secondaryOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })),
    /** 追加選択肢のラベル */
    secondaryLabel: PropTypes.string,
    /** 追加選択肢のプレースホルダー */
    secondaryPlaceholder: PropTypes.string,
    /** 追加選択肢のデフォルト項目ラベル */
    secondaryDefaultLabel: PropTypes.string,
};

export default PromptSelectModal;
